import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import OccupationService from '../services/occupationService';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Clear error function
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Load user profile from database
  const loadUserProfile = useCallback(async (userId) => {
    try {
      // Get the current user's email to match against user_profiles
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.warn('No authenticated user found');
        return null;
      }

      console.log('Loading profile for user:', user.email);

      // If this is the SkillBinder demo account, return mock profile immediately
      if (user.email === 'info@skillbinder.com') {
        console.log('Using SkillBinder demo profile fallback...');
        const demoProfile = {
          email: 'info@skillbinder.com',
          full_name: 'Glenn Donnelly',
          role: 'premium',
          subscription_status: 'active',
          occupation_id: null,
          seniority_level: null
        };
        
        console.log('Returning demo profile:', demoProfile);
        return demoProfile;
      }

      // Try to query user_profiles by email
      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('email', user.email)
          .single();

        if (error) {
          console.warn('Profile not found for email:', user.email, error.message);
          return null;
        }
        
        // If profile exists but first_name/last_name are empty, try to populate them from full_name
        if (data && (!data.first_name || !data.last_name) && data.full_name) {
          console.log('Profile found but missing first/last name, attempting to populate...');
          
          const nameParts = data.full_name.trim().split(' ');
          const firstName = nameParts[0] || '';
          const lastName = nameParts.slice(1).join(' ') || '';
          
          if (firstName && (firstName !== data.first_name || lastName !== data.last_name)) {
            console.log(`Updating profile with parsed names: "${firstName}" "${lastName}"`);
            
            try {
              const { error: updateError } = await supabase
                .from('user_profiles')
                .update({
                  first_name: firstName,
                  last_name: lastName
                })
                .eq('id', data.id);
              
              if (!updateError) {
                // Update the data object with the new values
                data.first_name = firstName;
                data.last_name = lastName;
                console.log('Successfully updated profile with first/last names');
              } else {
                console.warn('Failed to update profile with first/last names:', updateError);
              }
            } catch (updateErr) {
              console.warn('Error updating profile:', updateErr);
            }
          }
        }
        
        console.log('Loaded user profile:', data);
        return data;
      } catch (dbError) {
        console.warn('Database access error (likely RLS):', dbError.message);
        
        // Return a basic profile for any user if database access fails
        const fullName = user.user_metadata?.full_name || user.email.split('@')[0];
        const nameParts = fullName.trim().split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';
        
        return {
          email: user.email,
          first_name: firstName,
          last_name: lastName,
          full_name: fullName,
          role: 'free',
          subscription_status: 'active',
          occupation_id: null,
          seniority_level: null
        };
      }
    } catch (err) {
      console.error('Error loading user profile:', err);
      return null;
    }
  }, []);

  // Create user profile in database
  const createUserProfile = useCallback(async (userData, additionalData = {}) => {
    try {
      // Check if this is the specific SkillBinder demo account
      const isSkillBinderDemo = userData.email === 'info@skillbinder.com';
      
      // Use the actual database schema from the screenshot
      const profileData = {
        email: userData.email,
        first_name: isSkillBinderDemo ? 'Glenn' : (additionalData.first_name || userData.user_metadata?.first_name || ''),
        last_name: isSkillBinderDemo ? 'Donnelly' : (additionalData.last_name || userData.user_metadata?.last_name || ''),
        full_name: isSkillBinderDemo ? 'Glenn Donnelly' : (additionalData.full_name || userData.user_metadata?.full_name || `${additionalData.first_name || ''} ${additionalData.last_name || ''}`.trim() || userData.email?.split('@')[0]),
        phone: additionalData.phone || userData.user_metadata?.phone || null,
        role: isSkillBinderDemo ? 'premium' : 'free',
        subscription_status: 'active',
        occupation_id: null,
        seniority_level: null
      };

      console.log('Attempting to create profile with data:', profileData);

      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .upsert(profileData, {
            onConflict: 'email'
          })
          .select()
          .single();

        if (error) {
          console.error('Error upserting user profile:', error);
          throw error;
        }
        return data;
      } catch (dbError) {
        console.warn('Database access error (likely RLS), returning mock profile:', dbError.message);
        
        // Return the profile data we tried to create as a fallback
        return profileData;
      }
    } catch (err) {
      console.error('Error creating user profile:', err);
      
      // Return a basic profile as fallback
      const fullName = additionalData.full_name || userData.email?.split('@')[0];
      const nameParts = fullName.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      
      return {
        email: userData.email,
        first_name: firstName,
        last_name: lastName,
        full_name: fullName,
        role: 'free',
        subscription_status: 'active',
        occupation_id: null,
        seniority_level: null
      };
    }
  }, []);

  // Sign up function
  const signUp = useCallback(async (email, password, additionalData = {}) => {
    try {
      setError(null);
      setLoading(true);

      const { data, error } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/authentication?confirmed=true`,
          data: {
            full_name: additionalData.full_name || `${additionalData.first_name || ''} ${additionalData.last_name || ''}`.trim(),
            first_name: additionalData.first_name || '',
            last_name: additionalData.last_name || '',
            phone: additionalData.phone || ''
          }
        }
      });

      if (error) {
        throw error;
      }

      if (data.user) {
        // Check if email confirmation is required
        if (data.user.email_confirmed_at) {
          // User is already confirmed (auto-confirm is enabled)
          console.log('User auto-confirmed, creating profile immediately');
          try {
            const profile = await createUserProfile(data.user, additionalData);
            if (profile) {
              setUserProfile(profile);
            }
          } catch (profileError) {
            console.warn('Could not create profile immediately:', profileError);
          }
          setUser(data.user);
        } else {
          // Email confirmation is required
          console.log('Email confirmation required for:', data.user.email);
          // Don't set user or profile yet - wait for email confirmation
          // The user will be set when they click the confirmation link
        }
      }

      return { data, error: null };
    } catch (err) {
      const errorMessage = err.message || 'Sign up failed';
      setError(errorMessage);
      return { error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [createUserProfile]);

  // Sign in function
  const signIn = useCallback(async (email, password) => {
    try {
      console.log('Starting sign in for:', email);
      setError(null);
      setLoading(true);

      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password
      });

      if (error) {
        console.error('Supabase auth error:', error);
        throw error;
      }

      console.log('Auth successful, user:', data.user?.email);

      if (data.user) {
        setUser(data.user);
        
        // Load user profile with timeout
        try {
          console.log('Loading user profile...');
          const profile = await Promise.race([
            loadUserProfile(data.user.id),
            new Promise((_, reject) => setTimeout(() => reject(new Error('Profile load timeout')), 10000))
          ]);
          
          if (profile) {
            console.log('Profile loaded:', profile);
            setUserProfile(profile);
          } else {
            console.log('No profile found, creating new one...');
            // If no profile exists, create one
            try {
              const newProfile = await createUserProfile(data.user);
              if (newProfile) {
                console.log('New profile created:', newProfile);
                setUserProfile(newProfile);
              }
            } catch (profileError) {
              console.warn('Could not create profile:', profileError);
            }
          }
        } catch (profileError) {
          console.warn('Profile loading failed:', profileError);
          // Continue anyway - user is still authenticated
        }
      }

      console.log('Sign in completed successfully');
      return { data, error: null };
    } catch (err) {
      console.error('Sign in error:', err);
      const errorMessage = err.message || 'Sign in failed';
      setError(errorMessage);
      return { error: errorMessage };
    } finally {
      console.log('Setting loading to false');
      setLoading(false);
    }
  }, [loadUserProfile, createUserProfile]);

  // Sign out function
  const signOut = useCallback(async () => {
    try {
      // Sign out process started
      
      // Sign out from Supabase
      // Calling supabase.auth.signOut()
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error('Supabase signOut error:', error);
        throw error;
      }

      console.log('Supabase signOut successful, clearing local state...');
      // Clear local state
      setUser(null);
      setUserProfile(null);
      setError(null);
      
      console.log('Sign out successful - all state cleared');
    } catch (err) {
      console.error('Sign out failed:', err);
      // Even if Supabase signOut fails, clear local state
      console.log('Clearing local state despite Supabase error...');
      setUser(null);
      setUserProfile(null);
      throw err;
    }
  }, []);

  // Update user profile
  const updateProfile = useCallback(async (updates) => {
    if (!userProfile) {
      throw new Error('No user profile found');
    }

    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userProfile.user_id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      setUserProfile(data);
      return { data, error: null };
    } catch (err) {
      const errorMessage = err.message || 'Profile update failed';
      setError(errorMessage);
      return { error: errorMessage };
    }
  }, [userProfile]);

  // Initialize authentication state
  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      try {
        console.log('Initializing auth...');
        
        // Get initial session
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Session initialization error:', error);
          if (mounted) {
            setError(error.message);
            setLoading(false);
          }
          return;
        }

        console.log('Session loaded:', session?.user?.email || 'No user');

        if (mounted && session?.user) {
          setUser(session.user);
          
          // Load user profile with timeout
          try {
            const profile = await Promise.race([
              loadUserProfile(session.user.id),
              new Promise((_, reject) => setTimeout(() => reject(new Error('Profile load timeout')), 10000))
            ]);
            
            if (profile) {
              setUserProfile(profile);
              // Best-effort occupation prefetch after login
              // try { OccupationService.prefetchOccupations(profile.role || 'free'); } catch {}
            }
          } catch (profileError) {
            console.warn('Profile loading failed during init:', profileError);
          }
        }

        if (mounted) {
          console.log('Auth initialization complete, setting loading to false');
          setLoading(false);
        }
      } catch (err) {
        console.error('Auth initialization error:', err);
        if (mounted) {
          setError(err.message);
          setLoading(false);
        }
      }
    };

    // Add timeout to force loading to false
    const initTimeout = setTimeout(() => {
      if (mounted) {
        console.log('Auth init timeout - forcing loading to false');
        setLoading(false);
      }
    }, 5000);

    initializeAuth();

    return () => {
      clearTimeout(initTimeout);
    };

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mounted) return;

        if (session?.user) {
          setUser(session.user);
          
          // Load user profile
          const profile = await loadUserProfile(session.user.id);
          if (profile) {
            setUserProfile(profile);
            // try { OccupationService.prefetchOccupations(profile.role || 'free'); } catch {}
          } else {
            // Try to create a profile for the user
            try {
              const newProfile = await createUserProfile(session.user);
              if (newProfile) {
                setUserProfile(newProfile);
              }
            } catch (profileError) {
              console.warn('Could not create profile:', profileError);
            }
          }
        } else {
          setUser(null);
          setUserProfile(null);
        }
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [loadUserProfile]);

  // Computed values
  const isAuthenticated = !!user;
  const isPremium = userProfile?.role === 'premium' || user?.email === 'info@skillbinder.com' || user?.email === 'glenn@epoxydogs.com';
  const userRole = user?.email === 'glenn@epoxydogs.com' ? 'admin' : (userProfile?.role || 'free');

  const value = {
    // State
    user,
    userProfile,
    loading,
    error,
    
    // Computed
    isAuthenticated,
    isPremium,
    userRole,
    
    // Actions
    signIn,
    signUp,
    signOut,
    updateProfile,
    clearError
  };

  // Debug logging removed for performance

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;